import { useEffect, useState } from "react";
import { fetchLearningPath, fetchLearningPathInsight } from "../lib/api";
import AIInsightCard from "./AIInsightCard";
import { Badge } from "./ui/badge";
import { BookOpen, Briefcase, Award, GraduationCap, Users, Clock, TrendingUp, ChevronRight } from "lucide-react";

const TYPE_META = {
  course: { icon: BookOpen, label: "Eğitim", color: "sky" },
  project: { icon: Briefcase, label: "Proje", color: "amber" },
  book: { icon: BookOpen, label: "Kitap", color: "violet" },
  certification: { icon: Award, label: "Sertifika", color: "emerald" },
  coaching: { icon: Users, label: "Koçluk", color: "rose" },
};

const COLOR_CLASSES = {
  sky: { bg: "bg-sky-50", text: "text-sky-700", border: "border-sky-200", dot: "bg-sky-500" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" },
  violet: { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200", dot: "bg-violet-500" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
  rose: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", dot: "bg-rose-500" },
};

export default function LearningPathPanel({ employeeId, targetRoleId }) {
  const [data, setData] = useState(null);
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(true);

  useEffect(() => {
    if (!employeeId) return;
    let cancelled = false;
    setLoading(true);
    setAiLoading(true);
    setInsight(null);
    (async () => {
      try {
        const d = await fetchLearningPath(employeeId, targetRoleId);
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    (async () => {
      try {
        const ai = await fetchLearningPathInsight(employeeId, targetRoleId);
        if (!cancelled) setInsight(ai);
      } catch {
        if (!cancelled) setInsight(null);
      } finally {
        if (!cancelled) setAiLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [employeeId, targetRoleId]);

  const retryInsight = async () => {
    setAiLoading(true);
    try {
      const ai = await fetchLearningPathInsight(employeeId, targetRoleId);
      setInsight(ai);
    } catch {
      setInsight(null);
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="card-surface p-8" data-testid="learning-path-loading">
        <div className="space-y-3">
          <div className="h-4 bg-slate-200/60 rounded animate-pulse w-1/3" />
          <div className="h-3 bg-slate-100 rounded animate-pulse w-2/3" />
          <div className="h-32 bg-slate-100 rounded-xl animate-pulse mt-4" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="learning-path-panel">
      <div className="lg:col-span-2 space-y-5">
        {/* Header summary */}
        <div className="card-surface p-6">
          <div className="flex flex-wrap items-end gap-6">
            <div>
              <div className="label-eyebrow text-sky-600 mb-1.5">Öğrenme Yolu</div>
              <h3 className="font-display text-2xl font-semibold">
                {data.target_role.title} için <span className="text-sky-700">12 haftalık</span> plan
              </h3>
              <p className="text-sm text-slate-600 mt-1.5">
                {data.total_items} kaynak · {data.total_duration_weeks} hafta toplam yatırım
              </p>
            </div>
            <div className="ml-auto flex items-end gap-6">
              <div className="text-center">
                <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-1">Şu an</div>
                <div className="font-mono text-2xl font-semibold text-slate-700">%{data.current_readiness}</div>
              </div>
              <div className="self-center text-slate-300">
                <ChevronRight className="w-5 h-5" />
              </div>
              <div className="text-center">
                <div className="text-[0.65rem] uppercase tracking-wider text-emerald-600 mb-1">Plan sonu</div>
                <div className="font-mono text-2xl font-semibold text-emerald-700" data-testid="projected-readiness">
                  %{data.projected_readiness}
                </div>
                <div className="text-[0.65rem] text-emerald-600 mt-0.5">
                  <TrendingUp className="w-3 h-3 inline mr-0.5" />+{data.projected_boost} puan
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Groups by skill */}
        {data.groups?.map((grp) => (
          <div key={grp.skill_id} className="card-surface p-6" data-testid={`learning-group-${grp.skill_id}`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="label-eyebrow mb-1">{grp.cluster_name || "Beceri kümesi"}</div>
                <h4 className="font-display text-lg font-semibold flex items-center gap-2">
                  {grp.skill_name}
                  <Badge variant="outline" className="border-amber-500/40 text-amber-700 bg-amber-500/10 text-[0.7rem]">
                    +{grp.delta} seviye
                  </Badge>
                </h4>
                <div className="text-xs text-slate-500 mt-1">
                  Mevcut: {grp.current_level}/5 · Hedef: {grp.target_level}/5
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {grp.items.map((it) => {
                const meta = TYPE_META[it.type] || TYPE_META.course;
                const colors = COLOR_CLASSES[meta.color];
                const Icon = meta.icon;
                return (
                  <div
                    key={it.id}
                    data-testid={`learning-item-${it.id}`}
                    className={`flex items-start gap-3 rounded-xl border ${colors.border} ${colors.bg} p-4 transition hover:shadow-sm`}
                  >
                    <div className={`w-9 h-9 rounded-lg bg-white border ${colors.border} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-4 h-4 ${colors.text}`} strokeWidth={1.8} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-[0.65rem] uppercase tracking-wider font-medium ${colors.text}`}>
                          {meta.label}
                        </span>
                        {it.priority === "high" && (
                          <span className="text-[0.6rem] uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">
                            Öncelik
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-medium text-slate-900 leading-snug">{it.title}</div>
                      <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                        <span>{it.provider}</span>
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {it.duration_weeks} hafta
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" /> +{it.expected_level_gain} sv
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Timeline */}
        {data.timeline?.length > 0 && (
          <div className="card-surface p-6">
            <div className="label-eyebrow mb-4">Zaman Çizelgesi (haftalık)</div>
            <div className="space-y-2.5">
              {data.timeline.map((t, idx) => {
                const meta = TYPE_META[t.type] || TYPE_META.course;
                const colors = COLOR_CLASSES[meta.color];
                return (
                  <div key={idx} className="flex items-center gap-3 text-sm">
                    <span className="font-mono text-xs text-slate-500 w-24 flex-shrink-0">
                      Hafta {t.week_start}{t.week_end !== t.week_start ? `-${t.week_end}` : ""}
                    </span>
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colors.dot}`} />
                    <span className="text-slate-900 truncate">{t.title}</span>
                    <span className="ml-auto text-xs text-slate-500 flex-shrink-0">{t.skill_name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-5">
        <AIInsightCard title="Öğrenme Önerisi" loading={aiLoading} insight={insight} onRetry={retryInsight} />
      </div>
    </div>
  );
}
